import { AuthService } from '../auth/auth.service';

import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';

import { environment } from '../../environments/environment';

import { FcstRun } from './models/fcst-run.interface';
import { ObsRun } from './models/obs-run.interface';
import moment, { Moment } from 'moment';
import { throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

export interface RadarDtg {
  mapServer: string;
  info: string;
  slider: number;
  visible: boolean;
  isFcst: boolean;
  isArchive: boolean;
  issue: number;
}

@Injectable()
export class RadarService {
  private http = inject(HttpClient);
  private auth = inject(AuthService);

  radarImages = [];
  dtgLabelText: string;
  public dtgCurrent: number;
  public index = 0;
  isArchive = false;
  archiveDate: Moment;
  centerCity = { searchName: 'whole Area' };
  cellTrackingUrls = [];
  loading: boolean;
  cellTrackingData = {};
  controlIsInitialized = false;
  regions = [];

  centerWholeArea = {
    de: [9.8, 51.5],
    benelux: [4.5, 52.45],
    eu: [2.7213974, 50.7392588],
    es: [-3.5, 40],
    fr: [3.7973552, 45.8221641],
    ch: [8.4, 46.8],
  };
  private dataUrl = environment.url.dataArchive;

  selectedCitySignal = signal<any>(null);

  showDistanceLegend = computed(() => {
    const city = this.selectedCitySignal();
    return (
      city && city.searchName !== 'whole Area' && city.latitude !== undefined
    );
  });

  updateSelectedCity(city: any) {
    this.selectedCitySignal.set(city);
    this.centerCity = city;
  }

  getLatestObsRun(type: number) {
    /* console.log(
      'https://mapserver-api.metops.net/state/radar/obs/latest?type=' +
        type +
        '&range=48&trackFilter=true'
    ); */
    return this.http
      .get<
        ObsRun[]
      >('https://mapserver-api.metops.net/state/radar/obs/latest?type=' + type + '&range=48&trackFilter=true', { headers: { Authorization: 'Bearer -' } })
      .pipe(
        map(data => {
          return data;
        }),
        catchError(err => throwError(err))
      );
  }

  getObsRunBetweenDates(
    type: number,
    startDate: moment.Moment,
    endDate: moment.Moment
  ) {
    return this.http
      .get<
        ObsRun[]
      >(`https://mapserver-api.metops.net/state/radar/obs/runs?type=${type}&validStart=${startDate.toISOString()}&validEnd=${endDate.toISOString()}`, { headers: { Authorization: 'Bearer -' } })
      .pipe(
        map(data => {
          return data.sort(
            (a, b) => moment(a.dtg).valueOf() - moment(b.dtg).valueOf()
          );
        }),
        catchError(err => throwError(err))
      );
  }

  getLatestFcstRun(type: number) {
    /* console.log(
      'https://mapserver-api.metops.net/state/radar/fcst/latest?type=' +
        type +
        '&range=1'
    ); */
    return this.http
      .get<
        FcstRun[]
      >('https://mapserver-api.metops.net/state/radar/fcst/latest?type=' + type + '&range=2', { headers: { Authorization: 'Bearer -' } })
      .pipe(
        map(data => {
          return data;
        }),
        catchError(err => throwError(err))
      );
  }

  public getCenterCities(areas?): Array<any> {
    const customerDocument = this.auth.getCustomerDocument();
    if (customerDocument['customerId'] === '1') {
      return [
        { searchName: 'whole Area', country: 'de' },
        {
          searchName: 'Berlin',
          longitude: 13.3888599,
          latitude: 52.5170365,
          country: 'de',
        },
        {
          searchName: 'Rostock',
          longitude: 12.1286127,
          latitude: 54.0924445,
          country: 'de',
        },
        {
          searchName: 'Hamburg',
          longitude: 10.000654,
          latitude: 53.550341,
          country: 'de',
        },
        {
          searchName: 'Hannover',
          longitude: 9.7385532,
          latitude: 52.3744779,
          country: 'de',
        },
        {
          searchName: 'Leipzig',
          longitude: 12.3747329,
          latitude: 51.3406321,
          country: 'de',
        },
        {
          searchName: 'Dresden',
          longitude: 13.7381437,
          latitude: 51.0493286,
          country: 'de',
        },
        {
          searchName: 'Cottbus',
          longitude: 14.3357307,
          latitude: 51.7567447,
          country: 'de',
        },
        {
          searchName: 'Düsseldorf',
          longitude: 6.7763137,
          latitude: 51.2254018,
          country: 'de',
        },
        {
          searchName: 'Köln',
          longitude: 6.959974,
          latitude: 50.938361,
          country: 'de',
        },
        {
          searchName: 'Erfurt',
          longitude: 11.0287364,
          latitude: 50.9777974,
          country: 'de',
        },
        {
          searchName: 'Frankfurt/Main',
          longitude: 8.6820917,
          latitude: 50.1106444,
          country: 'de',
        },
        {
          searchName: 'Saarbrücken',
          longitude: 6.996379,
          latitude: 49.234362,
          country: 'de',
        },
        {
          searchName: 'Stuttgart',
          longitude: 9.183333,
          latitude: 48.783333,
          country: 'de',
        },
        {
          searchName: 'Nürnberg',
          longitude: 11.077298,
          latitude: 49.453872,
          country: 'de',
        },
        {
          searchName: 'München',
          longitude: 11.5753822,
          latitude: 48.1371079,
          country: 'de',
        },
        {
          searchName: 'Freiburg',
          longitude: 7.8494005,
          latitude: 47.9960901,
          country: 'de',
        },
      ];
    }
    const zoomPointsActive = [];
    const zoomPointsfromPast = [];
    let archiveMode = false;
    const wholeAreasPoints = [];
    if (areas) {
      for (const area of areas) {
        wholeAreasPoints.push({ searchName: 'whole Area', country: area });
      }
    } else {
      wholeAreasPoints.push({ searchName: 'whole Area' });
    }
    for (const product of customerDocument.radarProducts) {
      if (!product.active) {
        // deleted product
        continue;
      }
      archiveMode = !this.auth.isProductActive('radarProducts', product.id);
      for (const point of product.points) {
        if (product.region !== null) {
          point.country = product.region;
        } else {
          point.country = 'de';
        }
        if (
          archiveMode &&
          zoomPointsfromPast.filter(p => p.searchName === point.searchName)
            .length < 1
        ) {
          zoomPointsfromPast.push(point);
        } else if (
          !archiveMode &&
          zoomPointsActive.filter(p => p.searchName === point.searchName)
            .length < 1
        ) {
          zoomPointsActive.push(point);
        }
      }
    }
    const citiesPoints =
      zoomPointsActive.length > 0 ? zoomPointsActive : zoomPointsfromPast;
    const cities = wholeAreasPoints.concat(
      citiesPoints.sort((a, b) => (a.searchName < b.searchName ? -1 : 1))
    );
    if (cities.length > 0 && this.regions.length <= 1) {
      this.centerCity = cities[1];
    } else {
      this.centerCity = cities[0];
    }
    return cities;
  }
}
